import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Code,
  FileText,
  Calendar,
  User,
  Hash
} from 'lucide-react';
import { createTemplate, updateTemplate } from '../../../services/documentTemplateService';
import logger from '../../../utils/logger';

const INITIAL_FORM_DATA = {
  code: '',
  name: '',
  description: '',
  version: '1.0',
  versionDate: new Date().toISOString().split('T')[0],
  elaboratedBy: '',
  reviewedBy: '',
  approvedBy: '',
  effectiveDate: '',
  documentType: 'formato',
  process: '',
  numberPrefix: '',
  numberFormat: '',
  nextNumber: 1,
  headerTemplate: '',
  footerTemplate: '',
  bodySections: {
    includeClient: true,
    includePassengers: false,
    includeHotels: false,
    includeFlights: false,
    includeTours: false,
    includeTransfers: false,
    includePayment: false,
    includeAssistance: false
  },
  status: 'draft',
  retentionPeriod: 5,
  storageLocation: 'Servidor - Carpeta Documentos'
};

const DocumentTemplateForm = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isEditing = Boolean(template);

  useEffect(() => {
    if (template) {
      setFormData({
        ...template,
        versionDate: template.version_date || template.versionDate,
        elaboratedBy: template.elaborated_by || template.elaboratedBy || '',
        reviewedBy: template.reviewed_by || template.reviewedBy || '',
        approvedBy: template.approved_by || template.approvedBy || '',
        effectiveDate: template.effective_date || template.effectiveDate || '',
        documentType: template.document_type || template.documentType || 'formato',
        numberPrefix: template.number_prefix || template.numberPrefix || '',
        numberFormat: template.number_format || template.numberFormat || '',
        nextNumber: template.next_number || template.nextNumber || 1,
        headerTemplate: template.header_template || template.headerTemplate || '',
        footerTemplate: template.footer_template || template.footerTemplate || '',
        bodySections: template.body_sections || template.bodySections || INITIAL_FORM_DATA.bodySections,
        retentionPeriod: template.retention_period || template.retentionPeriod || 5,
        storageLocation: template.storage_location || template.storageLocation || ''
      });
    }
  }, [template]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSectionChange = (section, checked) => {
    setFormData(prev => ({
      ...prev,
      bodySections: {
        ...prev.bodySections,
        [section]: checked
      }
    }));
  };

  const validateForm = () => {
    if (!formData.code.trim()) {
      setError('El código es obligatorio');
      return false;
    }
    if (!formData.name.trim()) {
      setError('El nombre es obligatorio');
      return false;
    }
    if (!formData.version.trim()) {
      setError('La versión es obligatoria');
      return false;
    }
    if (!formData.versionDate) {
      setError('La fecha de versión es obligatoria');
      return false;
    }
    if (!formData.numberFormat || !formData.numberFormat.includes('#')) {
      setError('El formato de numeración debe contener al menos un #');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess(false);

    try {
      // El backend espera camelCase
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        version: formData.version,
        versionDate: formData.versionDate,
        elaboratedBy: formData.elaboratedBy,
        reviewedBy: formData.reviewedBy,
        approvedBy: formData.approvedBy,
        effectiveDate: formData.effectiveDate || null,
        documentType: formData.documentType,
        process: formData.process,
        numberPrefix: formData.numberPrefix,
        numberFormat: formData.numberFormat,
        nextNumber: formData.nextNumber,
        headerTemplate: formData.headerTemplate,
        footerTemplate: formData.footerTemplate,
        bodySections: formData.bodySections,
        status: formData.status,
        retentionPeriod: formData.retentionPeriod,
        storageLocation: formData.storageLocation
      };

      if (isEditing) {
        await updateTemplate(template.id, payload);
      } else {
        await createTemplate(payload);
      }

      setSuccess(true);
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
    } catch (err) {
      logger.error('Error saving template:', err);

      let errorMessage = 'Error al guardar la plantilla';

      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;

          // Traducir mensajes comunes del backend
          if (errorMessage.includes('versionDate') || errorMessage.includes('version_date')) {
            errorMessage = 'El campo "Fecha de Versión" es obligatorio';
          } else if (errorMessage.includes('code')) {
            errorMessage = 'El campo "Código" es obligatorio';
          } else if (errorMessage.includes('name')) {
            errorMessage = 'El campo "Nombre" es obligatorio';
          } else if (errorMessage.includes('numberFormat') || errorMessage.includes('number_format')) {
            errorMessage = 'El campo "Formato de Numeración" es obligatorio y debe contener #';
          }
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-7 h-7" />
                {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla de Documento'}
              </h2>
              <p className="text-blue-100 mt-1 text-sm">
                ISO 9001:2015 - Sistema de Gestión de Calidad
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Mensajes */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span>¡Plantilla guardada exitosamente!</span>
            </div>
          )}

          {/* Sección 1: Identificación */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Code className="w-5 h-5 text-blue-600" />
              Identificación del Documento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: FO-GC-01"
                  disabled={isEditing}
                />
                <p className="text-xs text-gray-500 mt-1">Código único según ISO 9001</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Voucher de Hotel"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe el propósito de este documento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Documento
                </label>
                <select
                  value={formData.documentType}
                  onChange={(e) => handleChange('documentType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="formato">Formato</option>
                  <option value="instructivo">Instructivo</option>
                  <option value="procedimiento">Procedimiento</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proceso
                </label>
                <input
                  type="text"
                  value={formData.process}
                  onChange={(e) => handleChange('process', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Gestión Comercial, Operaciones"
                />
              </div>
            </div>
          </div>

          {/* Sección 2: Control de Versiones */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Control de Versiones
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Versión *
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1.0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Versión *
                </label>
                <input
                  type="date"
                  value={formData.versionDate}
                  onChange={(e) => handleChange('versionDate', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Obligatorio. Fecha en que se creó/modificó esta versión
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Vigencia
                </label>
                <input
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => handleChange('effectiveDate', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">Borrador</option>
                  <option value="active">Activo</option>
                  <option value="obsolete">Obsoleto</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sección 3: Control de Calidad */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Control de Calidad (ISO 9001)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Elaboró
                </label>
                <input
                  type="text"
                  value={formData.elaboratedBy}
                  onChange={(e) => handleChange('elaboratedBy', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Revisó
                </label>
                <input
                  type="text"
                  value={formData.reviewedBy}
                  onChange={(e) => handleChange('reviewedBy', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aprobó
                </label>
                <input
                  type="text"
                  value={formData.approvedBy}
                  onChange={(e) => handleChange('approvedBy', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre completo"
                />
              </div>
            </div>
          </div>

          {/* Sección 4: Configuración de Numeración */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Hash className="w-5 h-5 text-blue-600" />
              Configuración de Numeración
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prefijo
                </label>
                <input
                  type="text"
                  value={formData.numberPrefix}
                  onChange={(e) => handleChange('numberPrefix', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="VH-"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formato de Numeración *
                </label>
                <input
                  type="text"
                  value={formData.numberFormat}
                  onChange={(e) => handleChange('numberFormat', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="VH-####"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Obligatorio. Use # para números (Ej: VH-####)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número Inicial
                </label>
                <input
                  type="number"
                  value={formData.nextNumber}
                  onChange={(e) => handleChange('nextNumber', parseInt(e.target.value) || 1)}
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Sección 5: Secciones del Documento */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Secciones a Incluir en el Documento
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(formData.bodySections).map(([section, checked]) => (
                <label key={section} className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => handleSectionChange(section, e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    {section.replace('include', '').replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Sección 6: Plantillas HTML */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Plantillas HTML
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Encabezado (Header)
                </label>
                <textarea
                  value={formData.headerTemplate}
                  onChange={(e) => handleChange('headerTemplate', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder='<div>Use variables: {{logoUrl}}, {{agencyName}}, {{taxIdNumber}}</div>'
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pie de Página (Footer)
                </label>
                <textarea
                  value={formData.footerTemplate}
                  onChange={(e) => handleChange('footerTemplate', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder='<div>Use variables: {{code}}, {{version}}, {{elaboratedBy}}</div>'
                />
              </div>
            </div>
          </div>

          {/* Sección 7: Almacenamiento */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Control de Almacenamiento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Período de Retención (años)
                </label>
                <input
                  type="number"
                  value={formData.retentionPeriod}
                  onChange={(e) => handleChange('retentionPeriod', parseInt(e.target.value) || 5)}
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación de Almacenamiento
                </label>
                <input
                  type="text"
                  value={formData.storageLocation}
                  onChange={(e) => handleChange('storageLocation', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Servidor - Carpeta Documentos"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer con botones */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            disabled={isSaving}
          >
            Cancelar
          </button>
          <motion.button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 font-medium flex items-center gap-2"
            whileHover={{ scale: isSaving ? 1 : 1.02 }}
            whileTap={{ scale: isSaving ? 1 : 0.98 }}
          >
            <Save className="w-5 h-5" />
            {isSaving ? 'Guardando...' : (isEditing ? 'Actualizar Plantilla' : 'Crear Plantilla')}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default DocumentTemplateForm;
